/**
 * Group Actions: the call taker decides, for each HWCG constituency, whether
 * this incident activates them, only notifies them, or needs nothing from them.
 * Recorded on the incident's `settings.hwcg` alongside the intake fields.
 */
export type HwcgGroupId =
  | 'eoc'
  | 'responsible_party'
  | 'mutual_aid'
  | 'service_provider';

export type HwcgGroupActionValue = 'activate' | 'notify' | 'no_action';

export type HwcgGroupActionSelection = HwcgGroupActionValue | null;

export type HwcgGroupActionsState = Record<
  HwcgGroupId,
  HwcgGroupActionSelection
>;

export const HWCG_GROUP_IDS: HwcgGroupId[] = [
  'eoc',
  'responsible_party',
  'mutual_aid',
  'service_provider',
];

/**
 * Contact rows belong to one of these. Responsible Party is a company chosen on
 * the incident.
 */
export const HWCG_CONTACT_LIST_IDS = [
  'eoc',
  'mutual_aid',
  'service_provider',
] as const;

export type HwcgContactListId = (typeof HWCG_CONTACT_LIST_IDS)[number];

export function splitMutualAidByCompany<
  T extends { memberCompany?: string | null },
>(
  members: T[],
  responsiblePartyCompany: string
): { responsibleParty: T[]; mutualAid: T[] } {
  const company = responsiblePartyCompany.trim().toLowerCase();
  if (!company) return { responsibleParty: [], mutualAid: members };
  const responsibleParty: T[] = [];
  const mutualAid: T[] = [];
  for (const member of members) {
    const memberCompany = member.memberCompany?.trim().toLowerCase() ?? '';
    if (memberCompany === company) responsibleParty.push(member);
    else mutualAid.push(member);
  }
  return { responsibleParty, mutualAid };
}

export const HWCG_GROUP_LABELS: Record<HwcgGroupId, string> = {
  eoc: 'EOC',
  responsible_party: 'Responsible Party',
  mutual_aid: 'Mutual Aid',
  service_provider: 'Service Provider',
};

export const HWCG_GROUP_ACTION_VALUES: HwcgGroupActionValue[] = [
  'activate',
  'notify',
  'no_action',
];

/** Mutual Aid and Service Provider can be notified, not activated. */
export const HWCG_NOTIFY_ONLY_GROUP_IDS: readonly HwcgGroupId[] = [
  'mutual_aid',
  'service_provider',
];

const HWCG_NOTIFY_ONLY_GROUP_ID_SET = new Set<HwcgGroupId>(
  HWCG_NOTIFY_ONLY_GROUP_IDS
);

const HWCG_NOTIFY_ONLY_ACTION_VALUES: HwcgGroupActionValue[] = [
  'notify',
  'no_action',
];

export function isHwcgNotifyOnlyGroup(groupId: HwcgGroupId): boolean {
  return HWCG_NOTIFY_ONLY_GROUP_ID_SET.has(groupId);
}

export function hwcgGroupActionValues(
  groupId: HwcgGroupId
): readonly HwcgGroupActionValue[] {
  return isHwcgNotifyOnlyGroup(groupId)
    ? HWCG_NOTIFY_ONLY_ACTION_VALUES
    : HWCG_GROUP_ACTION_VALUES;
}

export const HWCG_GROUP_ACTION_LABELS: Record<HwcgGroupActionValue, string> = {
  activate: 'Activate',
  notify: 'Notify',
  no_action: 'No Action',
};

export function createEmptyHwcgGroupActions(): HwcgGroupActionsState {
  return {
    eoc: null,
    responsible_party: null,
    mutual_aid: null,
    service_provider: null,
  };
}

export function setHwcgGroupAction(
  actions: HwcgGroupActionsState,
  groupId: HwcgGroupId,
  action: HwcgGroupActionValue
): HwcgGroupActionsState {
  if (isHwcgNotifyOnlyGroup(groupId) && action === 'activate') {
    return actions;
  }
  return { ...actions, [groupId]: action };
}

export function allGroupsMarked(actions: HwcgGroupActionsState): boolean {
  return HWCG_GROUP_IDS.every((groupId) => actions[groupId] !== null);
}

export function markedGroupLabels(
  actions: HwcgGroupActionsState,
  action: HwcgGroupActionValue
): string[] {
  return HWCG_GROUP_IDS.filter((groupId) => actions[groupId] === action).map(
    (groupId) => HWCG_GROUP_LABELS[groupId]
  );
}

/** Short rail summary, e.g. "2 activating, 1 notifying". */
export function groupActionsSummary(actions: HwcgGroupActionsState): string {
  const parts: string[] = [];
  const activating = markedGroupLabels(actions, 'activate').length;
  const notifying = markedGroupLabels(actions, 'notify').length;
  if (activating > 0) parts.push(`${activating} activating`);
  if (notifying > 0) parts.push(`${notifying} notifying`);
  if (parts.length === 0) return 'No group actions';
  return parts.join(', ');
}
